// Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V5
/**
 * WorkOrderAiAssistant — AI chat sidebar for the rack layout editor.
 *
 * Streams agent responses via SSE and renders:
 * • Coherent conversational text tokens
 * • Rich interactive card widgets for tool results (no JSON dumps!)
 * • Context-aware quick actions (pills)
 * • Transactional "Undo" mechanism to revert AI actions
 */
import { useRef, useState, useCallback, useEffect } from 'react'
import toast from 'react-hot-toast'
import { ChevronRight } from 'lucide-react'
import { streamWorkOrderAiAgent } from '../../utils/layoutApi'
import type { AgentEvent } from '../../types'

interface Props {
  dcCode: string
  woId: string
  workOrderItems?: Array<{
    id: string
    item_type: string
    description: string
    status: string
    metadata_json?: Record<string, any>
  }>
  readOnly?: boolean
  onClose: () => void
  onActionComplete?: () => void
}

interface Message {
  role: 'user' | 'assistant'
  text: string
  toolCalls?: Array<{
    name: string
    arguments: any
    result?: any
    ok?: boolean
    error?: string
  }>
}

export default function WorkOrderAiAssistant({ dcCode, woId, workOrderItems, readOnly, onClose, onActionComplete }: Props) {
  const [prompt, setPrompt] = useState('')
  const [messages, setMessages] = useState<Message[]>([])
  const [busy, setBusy] = useState(false)
  const [undoing, setUndoing] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [showActionLog, setShowActionLog] = useState(false)
  const [undoConfirmPending, setUndoConfirmPending] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let interval: any = null
    if (busy) {
      setSeconds(0)
      interval = setInterval(() => {
        setSeconds((s) => s + 1)
      }, 1000)
    } else {
      setSeconds(0)
    }
    return () => {
      if (interval) clearInterval(interval)
    }
  }, [busy])

  // Auto-scroll to bottom.
  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  const handleSubmit = async (e: React.FormEvent, customPrompt?: string) => {
    if (e) e.preventDefault()
    let userText = (customPrompt ?? prompt).trim()
    if (!userText || busy) return

    if (readOnly) {
      userText = `[РЕЖИМ ПЛАНИРОВАНИЯ — только анализ, не выполняй изменений] ${userText}`
    }

    setMessages((prev) => [...prev, { role: 'user', text: userText }])
    setPrompt('')
    setBusy(true)

    const history = messages.map((m) => {
      const base: any = { role: m.role, content: m.text }
      if (m.toolCalls && m.toolCalls.length > 0) {
        base.toolCalls = m.toolCalls
      }
      return base
    })

    // Create a placeholder assistant message.
    const assistantMsg: Message = { role: 'assistant', text: '', toolCalls: [] }
    setMessages((prev) => [...prev, assistantMsg])

    const abortCtrl = new AbortController()
    const startedAt = Date.now()
    // Silence budget, not total budget: the server pings while it thinks, so
    // this only fires when the stream really goes quiet. The server's `hello`
    // replaces the placeholder with the limit configured in admin.
    let silenceMs = 60_000
    let timeoutId: any = null
    const armTimeout = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => abortCtrl.abort(), silenceMs)
    }
    armTimeout()

    // Buffer for accumulating tool invocations.
    const pendingTools: Message['toolCalls'] = []
    assistantMsg.toolCalls = pendingTools
    let currentToolIndex = -1

    const onEvent = (ev: AgentEvent) => {
      if (ev.type === 'hello') {
        const configured = Number(ev.data?.timeout_seconds)
        if (configured > 0) silenceMs = configured * 1000
      }
      armTimeout()

      switch (ev.type) {
        case 'tool_call':
          pendingTools.push({
            name: ev.data.name,
            arguments: ev.data.arguments,
          })
          currentToolIndex = pendingTools.length - 1
          setMessages((prev) => [...prev])
          scrollToBottom()
          break

        case 'tool_result':
          if (currentToolIndex >= 0) {
            pendingTools[currentToolIndex].result = ev.data.result
            pendingTools[currentToolIndex].ok = ev.data.ok
            if (!ev.data.ok) {
              pendingTools[currentToolIndex].error = ev.data.result?.error ?? 'Ошибка выполнения'
            }
            setMessages((prev) => [...prev])
            scrollToBottom()
          }
          break

        case 'token':
          assistantMsg.text += ev.data
          setMessages((prev) => [...prev])
          scrollToBottom()
          break

        case 'error':
          assistantMsg.text += `\n❌ Ошибка: ${ev.data}`
          setMessages((prev) => [...prev])
          scrollToBottom()
          break

        case 'done':
          clearTimeout(timeoutId)
          setMessages((prev) => [...prev])
          setBusy(false)
          scrollToBottom()
          onActionComplete?.()
          break
      }
    }

    try {
      await streamWorkOrderAiAgent(dcCode, woId, userText, history, {
        signal: abortCtrl.signal,
        onEvent,
      })
      clearTimeout(timeoutId)
      // A stream that ends without `done` must not leave the spinner forever.
      setBusy(false)
    } catch (err: any) {
      clearTimeout(timeoutId)
      const isTimeout = err.name === 'AbortError'
      const waited = Math.round((Date.now() - startedAt) / 1000)
      const msg = isTimeout
        ? `⏱ Ответ не пришёл за ${waited} с — соединение прервано. Модель могла не успеть: повторите запрос или сформулируйте его короче.`
        : `⚠ Подключение потеряно: ${err.message}`
      assistantMsg.text += `\n\n${msg}`
      setMessages((prev) => [...prev])
      scrollToBottom()
      setBusy(false)
    }
  }

  // Determine if the last assistant step has undoable items
  const assistantMsgs = messages.filter(m => m.role === 'assistant' && m.toolCalls && m.toolCalls.length > 0)
  const lastAssistantMsg = assistantMsgs.length > 0 ? assistantMsgs[assistantMsgs.length - 1] : null
  const canUndo = lastAssistantMsg?.toolCalls?.some(tc => 
    tc.ok && (tc.name === 'draft_install_equipment' || tc.name === 'draft_auto_patch_equipment' || tc.name === 'draft_patch_cable')
  )

  const handleUndo = async () => {
    if (!lastAssistantMsg || !lastAssistantMsg.toolCalls || busy || undoing) return

    const eqIdsToDelete: string[] = []
    const cableIdsToDelete: string[] = []

    lastAssistantMsg.toolCalls.forEach(tc => {
      if (tc.ok && tc.result) {
        if (tc.name === 'draft_install_equipment') {
          const list = tc.result.installed_equipment || []
          list.forEach((eq: any) => {
            if (eq.id) eqIdsToDelete.push(eq.id)
          })
        } else if (tc.name === 'draft_auto_patch_equipment') {
          const list = tc.result.results || []
          list.forEach((server: any) => {
            if (server.conn_a?.cable_id) cableIdsToDelete.push(server.conn_a.cable_id)
            if (server.conn_b?.cable_id) cableIdsToDelete.push(server.conn_b.cable_id)
            if (server.conn_ilo?.cable_id) cableIdsToDelete.push(server.conn_ilo.cable_id)
          })
        } else if (tc.name === 'draft_patch_cable') {
          const id = tc.result.cable_id || tc.result.id
          if (id) cableIdsToDelete.push(id)
        }
      }
    })

    if (eqIdsToDelete.length === 0 && cableIdsToDelete.length === 0) {
      toast.error('Нет обратимых действий ИИ (установок или коммутаций) в последнем шаге.')
      return
    }

    // Two-click confirmation: first click shows warning toast, second click proceeds
    if (!undoConfirmPending) {
      setUndoConfirmPending(true)
      toast(
        `⚠ Отменить последнее действие ИИ? Удалит: ${eqIdsToDelete.length} уст. и ${cableIdsToDelete.length} каб. Нажмите «Откат» ещё раз для подтверждения.`,
        { duration: 5000, icon: '⚠️' }
      )
      setTimeout(() => setUndoConfirmPending(false), 5000)
      return
    }
    setUndoConfirmPending(false)

    setUndoing(true)
    try {
      const deletePromises: Promise<any>[] = []

      // Delete cables
      cableIdsToDelete.forEach(id => {
        deletePromises.push(
          fetch(`/api/cables/${id}?workspace=${woId}`, {
            method: 'DELETE',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'include'
          })
        )
      })

      // Delete equipment
      eqIdsToDelete.forEach(id => {
        deletePromises.push(
          fetch(`/api/equipment/${id}?workspace=${woId}`, {
            method: 'DELETE',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'include'
          })
        )
      })

      await Promise.all(deletePromises)

      // Remove the last assistant message and the preceding user message
      setMessages(prev => {
        const lastIdx = prev.lastIndexOf(lastAssistantMsg)
        if (lastIdx >= 1) {
          return prev.filter((_, idx) => idx !== lastIdx && idx !== lastIdx - 1)
        }
        return prev.filter((_, idx) => idx !== lastIdx)
      })

      onActionComplete?.()
    } catch (err: any) {
      console.error(err)
      toast.error(`Ошибка отката изменений: ${err.message}`)
    } finally {
      setUndoing(false)
    }
  }

  // Quick Action Prompts — dynamic from pending WO steps + static fallbacks
  const pendingSteps = (workOrderItems || []).filter(i => i.status === 'PENDING').slice(0, 3)
  const dynamicActions = pendingSteps.map(step => ({
    label: `${step.item_type === 'INSTALL_EQUIPMENT' ? '🖥' : step.item_type === 'PATCH_CABLE' ? '🔌' : step.item_type === 'REMOVE_EQUIPMENT' ? '🗑' : '📝'} ${step.description.slice(0, 40)}${step.description.length > 40 ? '...' : ''}`,
    prompt: `Выполни шаг наряда: ${step.description}`,
  }))
  const staticActions = [
    { label: '📋 Статус наряда', prompt: 'Покажи полный статус текущего наряда и что ещё предстоит сделать' },
    { label: '⚠️ SPOF-анализ', prompt: 'Проведи анализ отказоустойчивости питания (SPOF) в текущем датацентре' },
    { label: '🛡 Физический аудит', prompt: 'Проведи полный физический аудит изменений черновика перед слиянием' },
  ]
  const quickActions = [...dynamicActions, ...staticActions]

  // Render cards for tool results
  const renderToolCall = (tc: { name: string; arguments: any; result?: any; ok?: boolean; error?: string }, ti: number) => {
    const { name, arguments: args, result, ok, error } = tc

    if (error || (ok === false)) {
      return (
        <div key={ti} className="bg-status-critical/10 border border-status-critical/30 rounded-lg p-2.5 text-xs text-status-critical space-y-1 text-left">
          <div className="font-semibold flex items-center gap-1 text-status-critical">
            <span>❌ Ошибка: {name}</span>
          </div>
          <p className="text-[11px] text-status-critical/90 whitespace-pre-wrap">{error || 'Неизвестная ошибка выполнения'}</p>
        </div>
      )
    }

    if (result === undefined) {
      return (
        <div key={ti} className="bg-bg-input/60 border border-border/60 rounded-lg p-2.5 text-xs text-text-secondary flex items-center gap-2.5 animate-pulse text-left">
          <div className="w-3.5 h-3.5 border-2 border-accent/30 border-t-accent rounded-full animate-spin shrink-0" />
          <span>Выполняется: <span className="font-mono text-text-primary">{name}</span>...</span>
        </div>
      )
    }

    const renderIdBadge = (id: string | number) => (
      <span 
        className="inline-block font-mono bg-bg-panel hover:bg-bg-hover text-text-muted hover:text-text-primary px-1 py-0.5 rounded text-[10px] cursor-pointer select-all border border-border/40"
        title="Нажмите, чтобы выделить ID"
      >
        ID: {id}
      </span>
    )

    switch (name) {
      case 'search_catalog': {
        const items = Array.isArray(result) ? result : (result.installed_equipment || result.results || [])
        if (items.length === 0) {
          return <div key={ti} className="text-text-muted text-xs italic p-1 bg-bg-app border border-border rounded-lg text-left">Шаблоны не найдены</div>
        }
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2 text-xs text-left">
            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Каталог оборудования</div>
            <div className="divide-y divide-border max-h-48 overflow-y-auto pr-1">
              {items.map((item: any, i: number) => (
                <div key={i} className="py-2 first:pt-0 last:pb-0 flex flex-col gap-1">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-text-primary">{item.manufacturer} {item.model}</span>
                    <span className="text-[10px] bg-bg-input text-text-secondary px-1.5 py-0.5 rounded font-mono font-semibold">{item.u_height}U</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] text-text-muted">
                    <span className="capitalize">Тип: {item.equipment_type}</span>
                    {renderIdBadge(item.id)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'search_racks': {
        const racks = Array.isArray(result) ? result : []
        if (racks.length === 0) {
          return <div key={ti} className="text-text-muted text-xs italic p-1 bg-bg-app border border-border rounded-lg text-left">Стойки не найдены</div>
        }
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2 text-xs text-left">
            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Найденные стойки</div>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto pr-1">
              {racks.map((rack: any, i: number) => (
                <div key={i} className="bg-bg-panel/60 rounded-md border border-border p-2 flex flex-col justify-between gap-1.5">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-text-primary">{rack.name}</span>
                    {rack.is_blocked ? (
                      <span className="text-[9px] bg-status-critical/15 text-status-critical px-1 py-0.5 rounded">Блок</span>
                    ) : (
                      <span className="text-[9px] bg-status-normal/15 text-status-normal px-1 py-0.5 rounded">Свободна</span>
                    )}
                  </div>
                  <div className="text-[10px] text-text-secondary">{rack.u_slots} U-юнитов</div>
                  <div className="mt-0.5">{renderIdBadge(rack.id)}</div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'search_equipment': {
        const eqList = Array.isArray(result) ? result : []
        if (eqList.length === 0) {
          return <div key={ti} className="text-text-muted text-xs italic p-1 bg-bg-app border border-border rounded-lg text-left">Оборудование не найдено</div>
        }
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2 text-xs text-left">
            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Найденные устройства</div>
            <div className="divide-y divide-border max-h-48 overflow-y-auto pr-1">
              {eqList.map((eq: any, i: number) => (
                <div key={i} className="py-2 first:pt-0 last:pb-0 flex flex-col gap-1">
                  <div className="flex justify-between items-start">
                    <span className="font-medium text-text-primary truncate max-w-[180px]">{eq.name}</span>
                    <span className="text-[10px] bg-bg-input text-text-muted px-1.5 py-0.5 rounded shrink-0">Стойка {eq.rack_id}</span>
                  </div>
                  <div className="flex justify-between items-center text-[10px] text-text-muted">
                    <span>Тип: {eq.type || eq.equipment_type} ({eq.u_height}U) • U{eq.u_position}</span>
                    {renderIdBadge(eq.id)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'get_rack_free_space': {
        const freeU = result.free_intervals || []
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2 text-xs text-left">
            <div className="flex justify-between items-center text-[10px] font-semibold text-text-muted uppercase tracking-wider">
              <span>Емкость {result.rack_name || `Стойка ${result.rack_id}`}</span>
              <span className="bg-accent/15 text-accent px-2 py-0.5 rounded font-normal normal-case">Max подряд: {result.max_continuous_free_u}U</span>
            </div>
            {freeU.length === 0 ? (
              <div className="text-status-critical text-xs italic p-1 bg-status-critical/15 border border-status-critical/30 rounded">Шкаф полностью заполнен</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
                {freeU.map((interval: any, i: number) => (
                  <span key={i} className="bg-bg-input text-text-secondary border border-border px-2 py-1 rounded text-[11px] font-medium">
                    U{interval.start} - U{interval.end} ({interval.slots}U)
                  </span>
                ))}
              </div>
            )}
          </div>
        )
      }

      case 'draft_install_equipment': {
        const list = result.installed_equipment || []
        return (
          <div key={ti} className="bg-status-normal/10 border border-status-normal/30 rounded-lg p-2.5 text-xs text-text-secondary space-y-2 text-left">
            <div className="flex items-center gap-1.5 text-status-normal font-semibold text-[11px]">
              <span>✅ Установка завершена</span>
            </div>
            <div className="divide-y divide-border">
              {list.map((eq: any, i: number) => (
                <div key={i} className="py-2 first:pt-0 last:pb-0 flex flex-col gap-0.5">
                  <div className="font-medium text-text-primary">{eq.name}</div>
                  <div className="flex justify-between text-[10px] text-text-muted">
                    <span>Стойка {eq.rack_id} • Юнит U{eq.u_position} ({eq.u_height}U)</span>
                    {renderIdBadge(eq.id)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'draft_auto_patch_equipment': {
        const connList = result.results || []
        return (
          <div key={ti} className="bg-accent/10 border border-accent/30 rounded-lg p-2.5 text-xs text-text-secondary space-y-2.5 text-left">
            <div className="flex items-center justify-between text-accent font-semibold border-b border-border pb-1.5 text-[11px]">
              <span>🔌 Авто-коммутация выполнена</span>
              <span className="bg-accent/20 text-accent px-1.5 py-0.5 rounded text-[10px] font-normal">{connList.length * 3} кабелей</span>
            </div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {connList.map((server: any, i: number) => (
                <div key={i} className="bg-bg-panel border border-border rounded-lg p-2.5 space-y-2">
                  <div className="font-semibold text-text-primary text-[11px] truncate">{server.server_name}</div>
                  
                  {server.conn_a && (
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="flex items-center gap-1.5 truncate mr-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                        <span className="text-text-muted shrink-0">Data 1:</span>
                        <span className="text-text-secondary font-medium truncate max-w-[110px]">{server.conn_a.switch}</span>
                      </span>
                      <span className="text-text-muted font-mono shrink-0">{server.conn_a.port} ({server.conn_a.length}m)</span>
                    </div>
                  )}

                  {server.conn_b && (
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="flex items-center gap-1.5 truncate mr-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-status-warning shrink-0" />
                        <span className="text-text-muted shrink-0">Data 2:</span>
                        <span className="text-text-secondary font-medium truncate max-w-[110px]">{server.conn_b.switch}</span>
                      </span>
                      <span className="text-text-muted font-mono shrink-0">{server.conn_b.port} ({server.conn_b.length}m)</span>
                    </div>
                  )}

                  {server.conn_ilo && (
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="flex items-center gap-1.5 truncate mr-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-status-normal shrink-0" />
                        <span className="text-text-muted shrink-0">Mgmt:</span>
                        <span className="text-text-secondary font-medium truncate max-w-[110px]">{server.conn_ilo.switch}</span>
                      </span>
                      <span className="text-text-muted font-mono shrink-0">{server.conn_ilo.port} ({server.conn_ilo.length}m)</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'draft_patch_cable': {
        return (
          <div key={ti} className="bg-bg-input border border-border rounded-lg p-2.5 text-xs text-text-secondary space-y-1.5 text-left">
            <div className="font-semibold text-text-primary">🔌 Кабель подключен</div>
            <div className="text-[10px] text-text-muted flex flex-wrap gap-1 items-center">
              <span className="text-text-secondary">{args.port_a_id}</span>
              <span>⟷</span>
              <span className="text-text-secondary">{args.port_b_id}</span>
            </div>
            <div className="flex justify-between items-center text-[10px] text-text-muted pt-0.5">
              <span className="flex items-center gap-1 capitalize">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: result.color || 'var(--accent, #6366f1)' }} />
                {result.cable_type || 'CAT6'} ({result.length_m}м)
              </span>
              {renderIdBadge(result.cable_id || result.id)}
            </div>
          </div>
        )
      }

      case 'search_vendor_manuals': {
        const manuals = result.results || []
        if (manuals.length === 0) {
          return (
            <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 text-xs text-text-muted italic text-left">
              Инструкции не найдены
            </div>
          )
        }
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2.5 text-xs text-left">
            <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Инструкции и спецификации вендоров</div>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {manuals.map((m: any, idx: number) => (
                <div key={idx} className="bg-bg-input/60 rounded-md border border-border p-2 space-y-1">
                  <div className="font-semibold text-text-primary">{m.title}</div>
                  <div className="text-[10px] text-text-secondary font-medium">{m.manufacturer} {m.model || ''}</div>
                  <div className="text-[11px] text-text-secondary whitespace-pre-wrap leading-relaxed mt-1">{m.content}</div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'get_draft_validation_report': {
        const violations = result.violations || []
        const status = result.status || 'PASSED'
        
        let statusBadge = (
          <span className="bg-status-normal/15 text-status-normal px-2 py-0.5 rounded font-semibold text-[10px]">
            ПРОЙДЕН (PASSED)
          </span>
        )
        if (status === 'WARNING') {
          statusBadge = (
            <span className="bg-status-warning/15 text-status-warning px-2 py-0.5 rounded font-semibold text-[10px]">
              ПРЕДУПРЕЖДЕНИЯ (WARNING)
            </span>
          )
        } else if (status === 'BLOCKED') {
          statusBadge = (
            <span className="bg-status-critical/15 text-status-critical px-2 py-0.5 rounded font-semibold text-[10px]">
              БЛОКИРОВКА (BLOCKED)
            </span>
          )
        }
        
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2.5 text-xs text-left">
            <div className="flex justify-between items-center border-b border-border pb-1.5">
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Физический аудит черновика</span>
              {statusBadge}
            </div>
            {violations.length === 0 ? (
              <div className="text-status-normal text-xs italic p-1">Нарушений физических ограничений не обнаружено. Слияние разрешено.</div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {violations.map((v: any, idx: number) => {
                  let catBadgeColor = "bg-bg-input text-text-secondary"
                  let categoryLabel = v.category
                  
                  if (v.category === 'collision') {
                    catBadgeColor = "bg-status-critical/15 text-status-critical border border-status-critical/20"
                    categoryLabel = "размещение"
                  } else if (v.category === 'thermal') {
                    catBadgeColor = "bg-status-warning/15 text-status-warning border border-status-warning/20"
                    categoryLabel = "температура"
                  } else if (v.category === 'weight') {
                    catBadgeColor = "bg-status-warning/15 text-status-warning border border-status-warning/20"
                    categoryLabel = "вес"
                  } else if (v.category === 'phase') {
                    catBadgeColor = "bg-status-warning/15 text-status-warning border border-status-warning/20"
                    categoryLabel = "фазы"
                  } else if (v.category === 'redundancy') {
                    catBadgeColor = "bg-status-critical/15 text-status-critical border border-status-critical/20"
                    categoryLabel = "отказоустойчивость"
                  }
                  
                  return (
                    <div key={idx} className="bg-bg-panel border border-border rounded-md p-2 space-y-1.5">
                      <div className="flex items-center gap-1.5 justify-between">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold ${catBadgeColor}`}>
                          {categoryLabel}
                        </span>
                        <span className={`text-[9px] font-semibold ${v.severity === 'critical' ? 'text-status-critical' : 'text-status-warning'}`}>
                          {v.severity === 'critical' ? 'КРИТИЧЕСКАЯ' : 'ПРЕДУПРЕЖДЕНИЕ'}
                        </span>
                      </div>
                      <p className="text-text-primary text-[11px] leading-relaxed">{v.description}</p>
                      {v.affected_equipment_ids && v.affected_equipment_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1 items-center pt-0.5">
                          <span className="text-[9px] text-text-muted">Объекты:</span>
                          {v.affected_equipment_ids.map((id: string, iIdx: number) => (
                            <span key={iIdx} className="bg-bg-app px-1 py-0.5 rounded text-[9px] font-mono text-text-muted border border-border">
                              {id.substring(0, 8)}...
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      }

      case 'draft_commit_changes': {
        const status = result.status || 'success'
        const message = result.message || ''
        const auditReport = result.audit_report || {}
        const violations = auditReport.violations || []
        
        if (status === 'rejected') {
          return (
            <div key={ti} className="bg-status-critical/10 rounded-lg border border-status-critical/30 p-3 space-y-2.5 text-xs text-left">
              <div className="flex justify-between items-center border-b border-status-critical/20 pb-1.5">
                <span className="text-[10px] font-semibold text-status-critical uppercase tracking-wider">Слияние отклонено</span>
                <span className="bg-status-critical/15 text-status-critical px-2 py-0.5 rounded font-semibold text-[10px]">
                  BLOCKED
                </span>
              </div>
              <p className="text-status-critical text-[11px] leading-relaxed font-medium">{message}</p>
              
              {violations.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1 pt-1.5 border-t border-status-critical/20">
                  <div className="text-[9px] font-semibold text-status-critical/80 mb-1">Блокирующие нарушения:</div>
                  {violations.map((v: any, idx: number) => (
                    <div key={idx} className="bg-status-critical/10 border border-status-critical/20 rounded p-1.5 space-y-1">
                      <div className="flex items-center gap-1.5 justify-between">
                        <span className="px-1.5 py-0.5 bg-status-critical/15 text-status-critical text-[8px] rounded uppercase font-semibold">
                          {v.category}
                        </span>
                      </div>
                      <p className="text-status-critical/90 text-[10px] leading-relaxed">{v.description}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        }
        
        if (status === 'error') {
          return (
            <div key={ti} className="bg-status-critical/10 border border-status-critical/30 rounded-lg p-3 text-xs text-status-critical space-y-1 text-left">
              <div className="font-semibold text-status-critical">❌ Ошибка слияния черновика</div>
              <p className="text-[11px] text-status-critical/90 whitespace-pre-wrap">{message}</p>
            </div>
          )
        }

        return (
          <div key={ti} className="bg-status-normal/10 rounded-lg border border-status-normal/30 p-3 space-y-2.5 text-xs text-left">
            <div className="flex justify-between items-center border-b border-status-normal/20 pb-1.5">
              <span className="text-[10px] font-semibold text-status-normal uppercase tracking-wider">Слияние черновика</span>
              <span className="bg-status-normal/15 text-status-normal px-2 py-0.5 rounded font-semibold text-[10px]">
                SUCCESS
              </span>
            </div>
            <p className="text-status-normal text-[11px] leading-relaxed font-medium">{message}</p>
            <div className="text-[10px] text-text-muted">
              Все изменения физической топологии (стойки, оборудование, порты, кабельные линии) зафиксированы в основной базе данных. Резервирование ресурсов разблокировано.
            </div>
          </div>
        )
      }

      case 'get_work_order_context': {
        const steps = result.steps || []
        const prog = result.progress || {}
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2 text-xs text-left">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Наряд: {result.title}</span>
              <span className="text-[10px] font-mono text-accent">{prog.done}/{prog.total} выполнено</span>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {steps.map((s: any, i: number) => (
                <div key={i} className={`flex items-start gap-2 p-1.5 rounded border ${
                  s.status === 'DONE' ? 'border-status-normal/20 bg-status-normal/5' :
                  s.status === 'SKIPPED' ? 'border-border/30 opacity-60' :
                  'border-border/50 bg-bg-panel/60'
                }`}>
                  <span className="shrink-0 text-base">{s.status === 'DONE' ? '✅' : s.status === 'SKIPPED' ? '⏭' : '⏳'}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-[11px] truncate ${s.status !== 'PENDING' ? 'line-through text-text-muted' : 'text-text-primary'}`}>{s.description}</p>
                    {(s.equipment_model || s.rack) && (
                      <p className="text-[10px] text-text-muted">{s.equipment_model}{s.rack ? ` / ${s.rack}` : ''}{s.u_position ? ` @ U${s.u_position}` : ''}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'get_rack_equipment_summary': {
        const eqs = result.equipment || []
        return (
          <div key={ti} className="bg-bg-input/50 rounded-lg border border-border p-2.5 space-y-2 text-xs text-left">
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Стойка: {result.rack_name}</span>
              <span className="text-[10px] font-mono text-accent">{result.free_u_count} свободных U</span>
            </div>
            {result.free_ranges?.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {result.free_ranges.map((r: string, i: number) => (
                  <span key={i} className="text-[10px] bg-status-normal/10 text-status-normal px-1.5 py-0.5 rounded border border-status-normal/20">{r}</span>
                ))}
              </div>
            )}
            <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
              {eqs.map((eq: any, i: number) => (
                <div key={i} className="flex justify-between items-center text-[11px] py-1 border-b border-border/30 last:border-0">
                  <span className="text-text-primary truncate max-w-[160px]">{eq.name}</span>
                  <span className="text-text-muted shrink-0">U{eq.u_position} ({eq.u_height}U)</span>
                </div>
              ))}
            </div>
          </div>
        )
      }

      case 'get_equipment_swap_impact': {
        const impact = result.impact || {}
        const rec = result.recommendation || 'REVIEW_REQUIRED'
        const recColor = rec === 'SAFE' ? 'text-status-normal' : rec === 'BLOCKED' ? 'text-status-critical' : 'text-status-warning'
        const recBg = rec === 'SAFE' ? 'bg-status-normal/10 border-status-normal/30' : rec === 'BLOCKED' ? 'bg-status-critical/10 border-status-critical/30' : 'bg-status-warning/10 border-status-warning/30'
        return (
          <div key={ti} className={`rounded-lg border p-2.5 space-y-2 text-xs text-left ${recBg}`}>
            <div className="flex justify-between items-center">
              <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider">Анализ замены</span>
              <span className={`text-[10px] font-semibold ${recColor}`}>{rec}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div className="space-y-1">
                <p className="text-text-muted text-[10px]">Текущее</p>
                <p className="font-medium text-text-primary truncate">{result.current_equipment?.name}</p>
                <p className="text-text-muted">{result.current_equipment?.u_height}U · {result.current_equipment?.power_w}W</p>
              </div>
              <div className="space-y-1">
                <p className="text-text-muted text-[10px]">Новое</p>
                <p className="font-medium text-text-primary truncate">{result.new_catalog?.name}</p>
                <p className="text-text-muted">{result.new_catalog?.u_height}U · {result.new_catalog?.power_w}W</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 text-[10px]">
              {impact.u_delta !== 0 && <span className={`px-1.5 py-0.5 rounded border ${impact.u_delta > 0 ? 'bg-status-warning/10 text-status-warning border-status-warning/20' : 'bg-status-normal/10 text-status-normal border-status-normal/20'}`}>ΔU {impact.u_delta > 0 ? '+' : ''}{impact.u_delta}</span>}
              {impact.power_delta_w !== 0 && <span className="px-1.5 py-0.5 rounded border bg-bg-input text-text-muted border-border">ΔP {impact.power_delta_w > 0 ? '+' : ''}{impact.power_delta_w}W</span>}
              {impact.cables_at_risk_count > 0 && <span className="px-1.5 py-0.5 rounded border bg-status-critical/10 text-status-critical border-status-critical/20">⚡ {impact.cables_at_risk_count} кабелей под угрозой</span>}
              {impact.lost_ports?.length > 0 && <span className="px-1.5 py-0.5 rounded border bg-status-warning/10 text-status-warning border-status-warning/20">−{impact.lost_ports.length} портов</span>}
            </div>
            {impact.fit_issue && <p className="text-[11px] text-status-critical">{impact.fit_issue}</p>}
          </div>
        )
      }

      default:
        return (
          <details key={ti} className="bg-bg-input/60 rounded-lg border border-border p-2 text-xs text-left">
            <summary className="cursor-pointer text-text-secondary hover:text-text-primary select-none">
              🔧 {name} (результат JSON)
            </summary>
            <pre className="mt-1.5 text-[10px] text-text-muted overflow-x-auto bg-bg-app p-2 rounded font-mono">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        )
    }
  }

  return (
    <div className="flex flex-col h-full bg-bg-panel border-l border-border">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-bg-input shrink-0">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-text-primary">AI-инженер</h3>
          {canUndo && (
            <button
              onClick={handleUndo}
              disabled={undoing || busy}
              className="text-[11px] bg-bg-hover hover:bg-bg-active disabled:opacity-50 text-text-primary px-2 py-0.5 rounded transition-all duration-150 flex items-center gap-1 font-medium hover:scale-[1.02] border border-border"
            >
              {undoing ? 'Отмена...' : '↩ Отменить действие'}
            </button>
          )}
        </div>
        <button onClick={onClose} className="text-text-muted hover:text-text-primary text-lg p-2 rounded-lg focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none">
          ✕
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {readOnly && (
          <div className="mx-3 mb-3 px-3 py-2 bg-bg-input/60 border border-border/40 rounded-xl text-xs text-text-muted flex items-center gap-2">
            <span>👁</span> Режим просмотра — наряд в стадии планирования. Изменения не применяются.
          </div>
        )}
        {messages.length === 0 && (
          <div className="flex flex-col h-full justify-between py-6">
            <div className="text-center text-text-muted text-sm mt-8 space-y-2">
              <div className="text-[10px] font-mono text-text-muted tracking-wider uppercase mb-1">Ассистент наряда</div>
              <p>Спросите о стойках, свободных U, питании</p>
              <p>или о шагах этого наряда.</p>
              <p className="pt-4 text-xs text-text-muted">
                AI использует те же бэкенд-алгоритмы, что и физический редактор.
              </p>
            </div>

            {/* Quick Actions Panel */}
            <div className="space-y-2 px-2">
              <div className="text-[10px] font-semibold text-text-muted uppercase tracking-wider px-1">Быстрые действия</div>
              <div className="flex flex-col gap-2">
                {quickActions.map((qa, i) => (
                  <button
                    key={i}
                    onClick={(e) => handleSubmit(e, qa.prompt)}
                    className="bg-bg-input hover:bg-bg-hover border border-border text-text-secondary text-xs px-3 py-2.5 rounded-xl cursor-pointer text-left transition-all duration-150 flex items-center justify-between"
                  >
                    <span>{qa.label}</span>
                    <span className="text-text-muted text-[10px]">➤</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div key={idx} className={msg.role === 'user' ? 'text-right' : ''}>
            <div
              className={`inline-block max-w-[90%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-accent text-white rounded-br-none'
                  : 'bg-bg-input text-text-primary rounded-bl-none border border-border/40 shadow-md text-left'
              }`}
            >
              <div className="whitespace-pre-wrap">{msg.text || '...'}</div>

              {/* Collapsible/Rich rendering of tool calls */}
              {msg.toolCalls && msg.toolCalls.length > 0 && (
                <div className="mt-2.5 space-y-2 text-left">
                  {msg.toolCalls.map((tc, ti) => renderToolCall(tc, ti))}
                </div>
              )}
            </div>
            <div ref={scrollRef} />
          </div>
        ))}

        {busy && (
          <div className="flex items-center gap-2 p-3 bg-bg-input/80 rounded-xl text-text-secondary text-xs border border-border animate-pulse">
            <div className="w-3.5 h-3.5 border-2 border-accent/30 border-t-accent rounded-full animate-spin shrink-0" />
            <span>ИИ размышляет и выполняет операции... ({seconds < 60 ? `${seconds} сек` : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`})</span>
          </div>
        )}
      </div>

      {/* Action Log */}
      {(() => {
        const actionLog = messages.flatMap(m => m.toolCalls || []).filter(tc => tc.name && tc.ok === true)
        if (actionLog.length === 0) return null
        return (
          <div className="border-t border-border/30 px-3 py-2 shrink-0">
            <button
              onClick={() => setShowActionLog(s => !s)}
              className="w-full flex items-center justify-between text-xs text-text-muted hover:text-text-primary transition-colors"
            >
              <span>История ({actionLog.length} действий)</span>
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${showActionLog ? 'rotate-90' : ''}`} />
            </button>
            {showActionLog && (
              <ul className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                {actionLog.map((tc, i) => (
                  <li key={i} className="text-[10px] text-text-muted font-mono flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-status-normal shrink-0" />
                    {tc.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })()}

      {/* Input Form */}
      <form onSubmit={(e) => handleSubmit(e)} className="p-3 border-t border-border bg-bg-input shrink-0">
        <div className="flex gap-2">
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={readOnly ? 'Задайте вопрос по наряду...' : 'Спросите о ЦОД...'}
            disabled={busy}
            className="flex-1 px-3.5 py-2.5 bg-bg-app text-text-primary rounded-xl border border-border text-sm focus:border-accent focus:ring-1 focus:ring-accent/30 outline-none disabled:opacity-50 transition-all"
          />
          <button
            type="submit"
            disabled={busy || !prompt.trim()}
            className="px-4 py-2.5 bg-accent text-white rounded-xl text-sm font-semibold hover:bg-accent-hover active:bg-accent disabled:opacity-50 transition-all shadow-md"
          >
            {busy ? '…' : '➤'}
          </button>
        </div>
      </form>
    </div>
  )
}
